import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { CustomerModel } from '../../models/customer.model';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import * as fromApp from '../../store/app.reducer';
import * as AuthActions from '../../auth/store/auth.actions';
import { Subscription, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as CompanyActions from '../company/store/company.actions';
import { QrCodeComponent } from 'ng-qrcode';
import CryptoJS from 'crypto-js';
import { EmployeeModel } from 'src/app/models/employee.model';
import { CommonService } from 'src/app/services/common.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        TranslateModule,
        QrCodeComponent,
        MatTooltipModule
    ]
})
export class OnboardingComponent implements OnInit, OnDestroy {
    public employee: EmployeeModel | null = null;
    orderMethod: 'company' | 'person' = 'company';
    businessNumber: string = '';
    companyFormStep: number = 1;
    personFormStep: number = 1;
    searchResults: CustomerModel[] = [];
    selectedCompany: CustomerModel | null = null;
    searchError: string | null = null;

    // BankID states
    loading: boolean = false;
    qrCodeUrl: string | null = null;
    bankIdStatus: string | null = null; // e.g., 'pending', 'complete', 'failed', 'outstandingTransaction'
    bankIdUserDetails: any = null;

    private qrCodeRefreshTimerSub: Subscription | null = null; // Renamed to clearly denote Subscription
    private authRefreshTimerSub: Subscription | null = null; // Renamed
    private readonly QR_CODE_REFRESH_INTERVAL = 1000; // 1 second
    private readonly QR_CODE_MAX_AGE = 30000; // 30 seconds
    private readonly AUTH_REFRESH_INTERVAL = 60000; // 60 seconds (for re-initiating auth if no progress)
    private storeSub: Subscription | null = null; // Initialize to null
    private bankIdStartTime: Date | null = null;
    private isRefreshing: boolean = false;

    constructor(
        private router: Router,
        private store: Store<fromApp.AppState>,
        private cdr: ChangeDetectorRef,
        private commonService: CommonService
    ) { }

    ngOnInit(): void {
        this.checkUserAccess();
        this.storeSub = this.store.select(state => state.auth).subscribe(authState => {

            this.employee = authState.employee;
            this.loading = authState.loading;
            this.bankIdStatus = authState.bankIdStatus || 'pending';
            this.bankIdUserDetails = authState.bankIdUserDetails;

            if (authState.bankIdQrStartToken && authState.bankIdQrStartSecret && authState.bankIdQrAutoStartToken) {
                if (!this.bankIdStartTime) {
                    this.bankIdStartTime = new Date();
                }
                this.updateQrCode(authState.bankIdQrStartToken, authState.bankIdQrStartSecret, authState.bankIdQrAutoStartToken);

                // Start timers only if we're in pending state and not refreshing
                if ((!this.bankIdStatus || this.bankIdStatus === 'pending' || this.bankIdStatus === 'outstandingTransaction') && !this.isRefreshing) {
                    this.startQrCodeRefreshTimer();
                    this.startAuthRefreshTimer(); // This timer ensures we re-auth if no collect progress
                }
            }

            // Handle BankID completion (triggered by AuthEffects dispatching COLLECT_COMPLETE)
            if (this.bankIdStatus === 'complete' && this.bankIdUserDetails) {
                this.personFormStep = 2;
                this.cleanupTimers();
            }
        });

        // Subscribe to company search results
        this.storeSub.add(
            this.store.select(state => state.company).subscribe(companyState => {
                this.searchResults = companyState.searchResults;
                this.searchError = companyState.searchError;
                this.cdr.detectChanges();
            })
        );
    }

    ngOnDestroy(): void {
        if (this.storeSub) {
            this.storeSub.unsubscribe();
        }
        this.cleanupTimers();
    }

    private checkUserAccess(): void {
        this.store.select(state => state.auth).pipe(
            filter(authState => authState.authChecked),
            take(1)
        ).subscribe(authState => {
            if (!authState.employee) {
                this.router.navigate(['/login']);
            } else if (authState.employee.customers?.length > 0) {
                this.router.navigate(['/components/assignments']);
            }
        });
    }

    generateBankIdQR(qrStartToken: string, qrStartSecret: string, startedAt: Date, bankIdQrAutoStartToken: string): string {
        const currentTime = new Date();
        const timeStamp = Math.floor((currentTime.getTime() - startedAt.getTime()) / 1000);

        // Ensure timestamp is not negative
        if (timeStamp < 0) {
            this.bankIdStartTime = new Date();
            return this.generateBankIdQR(qrStartToken, qrStartSecret, this.bankIdStartTime!, bankIdQrAutoStartToken);
        }

        // Convert timestamp to string for HMAC calculation
        const timeStampStr = timeStamp.toString();

        // Calculate HMAC using qrStartSecret as key and timeStamp as message
        const hmac = CryptoJS.HmacSHA256(timeStampStr, qrStartSecret);
        // Format according to BankID specification: "bankid.qrStartToken.time.qrAuthCode"
        const qrCode = `bankid.${qrStartToken}.${timeStampStr}.${hmac}`;
        return qrCode;
    }

    private startQrCodeRefreshTimer(): void {
        this.stopQrCodeRefreshTimer();

        // Start immediately, then every second
        this.qrCodeRefreshTimerSub = timer(0, this.QR_CODE_REFRESH_INTERVAL).subscribe(() => {
            // Don't update if there are errors, completion, or during refresh
            if (this.bankIdStatus === 'complete' || this.isRefreshing) {
                this.stopQrCodeRefreshTimer();
                return;
            }

            if (!this.bankIdStartTime) {
                this.refreshBankIdAuth();
                return;
            }

            const qrAge = Date.now() - this.bankIdStartTime.getTime();
            if (qrAge >= this.QR_CODE_MAX_AGE) {
                console.log('QR code expired (30 seconds), refreshing BankID auth');
                this.refreshBankIdAuth();
                return;
            }

            // Get latest auth state for QR code generation
            this.store.select(state => state.auth).pipe(take(1)).subscribe(authState => {
                if (!authState.bankIdQrStartToken || !authState.bankIdQrStartSecret || !authState.bankIdQrAutoStartToken) {
                    this.refreshBankIdAuth();
                    return;
                }

                try {
                    const newQrCode = this.generateBankIdQR(
                        authState.bankIdQrStartToken,
                        authState.bankIdQrStartSecret,
                        this.bankIdStartTime!,
                        authState.bankIdQrAutoStartToken
                    );

                    if (newQrCode !== this.qrCodeUrl) {
                        this.qrCodeUrl = newQrCode;
                    }
                } catch (error) {
                    console.error('Error generating QR code:', error);
                    this.refreshBankIdAuth();
                }
            });
        });
    }

    stopQrCodeRefreshTimer(): void {
        if (this.qrCodeRefreshTimerSub) {
            this.qrCodeRefreshTimerSub.unsubscribe();
            this.qrCodeRefreshTimerSub = null;
        }
    }

    startAuthRefreshTimer(): void {
        this.stopAuthRefreshTimer();
        this.authRefreshTimerSub = timer(this.AUTH_REFRESH_INTERVAL).subscribe(() => {
            // This timer is a fallback to re-initiate auth if no progress is made
            if ((!this.bankIdStatus || this.bankIdStatus === 'pending' || this.bankIdStatus === 'outstandingTransaction') && !this.isRefreshing) {
                this.refreshBankIdAuth();
            }
        });
    }

    stopAuthRefreshTimer(): void {
        if (this.authRefreshTimerSub) {
            this.authRefreshTimerSub.unsubscribe();
            this.authRefreshTimerSub = null;
        }
    }

    refreshBankIdAuth(): void {
        if (this.isRefreshing) return;
        this.isRefreshing = true;
        this.cleanupTimers();

        this.bankIdStartTime = null;
        this.bankIdStatus = 'pending';
        this.qrCodeUrl = null;
        this.bankIdUserDetails = null;

        this.store.dispatch(new AuthActions.BankIdAuthClear());
        this.store.dispatch(new AuthActions.BankIdAuthStart());

        setTimeout(() => {
            this.isRefreshing = false;
        }, 3000);
    }

    setOrderMethod(method: 'company' | 'person'): void {
        if (this.orderMethod === method) {
            return;
        }

        const previousMethod = this.orderMethod;
        this.orderMethod = method;

        // Always cleanup timers and state
        this.cleanupTimers();
        this.store.dispatch(new AuthActions.BankIdAuthClear());
        this.bankIdStartTime = null;
        this.bankIdStatus = null;
        this.qrCodeUrl = null;
        this.bankIdUserDetails = null;
        this.isRefreshing = false;

        this.searchError = null;

        if (method === 'company') {
            this.companyFormStep = 1;
            this.searchResults = [];
            this.selectedCompany = null;
        } else if (method === 'person') {
            this.businessNumber = '';
            this.personFormStep = 1;

            // Only start BankID auth if we're not switching rapidly
            if (previousMethod !== 'person') {
                // Add a delay to ensure state is fully cleared
                setTimeout(() => {
                    // Double check we're still in person mode
                    if (this.orderMethod === 'person' && !this.isRefreshing) {
                        this.bankIdStartTime = new Date();
                        this.store.dispatch(new AuthActions.BankIdAuthStart());
                    }
                }, 500); // Increased delay for better state cleanup
            }
        }
    }

    searchBusiness(): void {
        if (this.businessNumber.length > 6) {
            this.store.dispatch(new CompanyActions.SearchCompany({ query: this.businessNumber }));
        } else {
            this.searchResults = [];
        }
    }

    selectCompany(company: CustomerModel): void {
        this.selectedCompany = company;
        this.companyFormStep = 2;
    }

    searchAgain(): void {
        this.companyFormStep = 1;
        this.selectedCompany = null;
        this.businessNumber = '';
        this.searchResults = [];
    }

    confirmCompanyDetails(): void {
        if (!this.selectedCompany) return;

        // Create FormData object to match the expected payload
        const formData = new FormData();
        formData.append('idNumber', this.selectedCompany.idNumber);
        formData.append('companyName', this.selectedCompany.companyName);
        formData.append('contactPerson', this.employee?.firstName + ' ' + this.employee?.lastName || this.selectedCompany?.contactPerson || '');
        formData.append('email', this.employee?.email || this.selectedCompany.email || '');
        formData.append('phone', this.employee?.phone || this.selectedCompany.phone || '');
        formData.append('address', this.selectedCompany.address ?? '');
        formData.append('city', this.selectedCompany.city ?? '');
        formData.append('biography', this.selectedCompany.biography ?? '');
        formData.append('customerWebsite', this.selectedCompany.customerWebsite ?? '');

        this.store.dispatch(new AuthActions.CreateCustomer(formData));

        const createCustomerSub = this.store.select(state => state.auth).subscribe(authState => {
            if (authState.customer) {
                setTimeout(() => {
                    this.router.navigate(['/components/assignments/create-assignment/step1']);
                }, 2000);
                createCustomerSub.unsubscribe();
            }
        });
        this.storeSub?.add(createCustomerSub);
    }

    onContinueClick(): void {
        if (!this.bankIdUserDetails) return;

        const formData = new FormData();
        formData.append('idNumber', this.bankIdUserDetails.personalNumber);
        formData.append('companyName', `${this.bankIdUserDetails.firstName} ${this.bankIdUserDetails.lastName}`);
        formData.append('email', this.bankIdUserDetails.email);
        formData.append('phone', this.bankIdUserDetails.phone);
        formData.append('address', this.bankIdUserDetails.address || '');
        formData.append('city', this.bankIdUserDetails.city || '');
        formData.append('country', this.bankIdUserDetails.country || '');
        formData.append('contactPerson', `${this.bankIdUserDetails.firstName} ${this.bankIdUserDetails.lastName}`);

        this.store.dispatch(new AuthActions.CreateCustomer(formData));

        const createCustomerSub = this.store.select(state => state.auth).subscribe(authState => {
            if (authState.customer) {
                setTimeout(() => {
                    this.router.navigate(['/components/assignments/create-assignment/step1']);
                }, 2000);
                createCustomerSub.unsubscribe();
            }
        });
        this.storeSub?.add(createCustomerSub);
    }

    closeOnboarding(): void {
        sessionStorage.clear();
        localStorage.clear();
        this.router.navigate(['/login']);
    }

    private updateQrCode(bankIdQrStartToken: string, bankIdQrStartSecret: string, bankIdQrAutoStartToken: string): void {
        if (!this.bankIdStartTime || this.isRefreshing) {
            return;
        }

        try {
            const newQrCode = this.generateBankIdQR(bankIdQrStartToken, bankIdQrStartSecret, this.bankIdStartTime!, bankIdQrAutoStartToken);
            if (newQrCode !== this.qrCodeUrl) {
                this.qrCodeUrl = newQrCode;
            }
        } catch (error) {
            console.error('Error in initial QR code generation:', error);
            this.refreshBankIdAuth();
        }
    }

    private cleanupTimers(): void {
        this.stopQrCodeRefreshTimer();
        this.stopAuthRefreshTimer();
        this.isRefreshing = false;
        this.qrCodeUrl = null;
    }
}